import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ImpressionContext } from '@nerdwallet/analytics/react/impression';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import MarketplaceApplyNow, { APPLY_NOW_LINK_TYPES, LINK_TYPES, APPLY_NOW_FINISH_TYPES } from '@nerdwallet/react-marketplace-apply-now';
import { CompareCardCheckbox, CHECKBOX_STYLE } from '@nerdwallet/compare-credit-cards-bar';
import { getLegacyId } from '@nerdwallet/marketplace/selectors/credit-cards';
import { TRK_PRODUCT_LOCATION, COMPONENT_ANALYTICS_NAME } from './lib/constants';
import { buildTrkSectionCategory, getMonetizable } from './lib/utils';
import styles from './marketplace-product-card.module.less';

var ProductCtas = function ProductCtas(_ref) {
  var _analytics$productImp, _analytics$productImp2, _analytics$productImp3, _analytics$productImp4;

  var _handleCcCompareClick = _ref._handleCcCompareClick,
      _hasCcCompareButton = _ref._hasCcCompareButton,
      analytics = _ref.analytics,
      className = _ref.className,
      ctaHref = _ref.ctaHref,
      ctaShowIcon = _ref.ctaShowIcon,
      ctaSubtext = _ref.ctaSubtext,
      ctaText = _ref.ctaText,
      displayPage = _ref.displayPage,
      enableAnalytics = _ref.enableAnalytics,
      id = _ref.id,
      marketplaceEntity = _ref.marketplaceEntity,
      reviewLinkHref = _ref.reviewLinkHref,
      secondaryCtaHref = _ref.secondaryCtaHref,
      secondaryCtaText = _ref.secondaryCtaText,
      shouldLogImpression = _ref.shouldLogImpression,
      taxonomy = _ref.taxonomy,
      trkPageType = _ref.trkPageType,
      trkProductPosition = _ref.trkProductPosition,
      trkSectionCategory = _ref.trkSectionCategory,
      _ccCompareButtonTextBeforeClick = _ref._ccCompareButtonTextBeforeClick,
      _ccCompareButtonTextAfterClick = _ref._ccCompareButtonTextAfterClick;
  var impressionContext = useContext(ImpressionContext);
  useDeprecatedPropsWarning({
    component: 'ProductCtas',
    deprecatedProps: [{
      version: '6.9.0',
      deprecated: 'enableAnalytics',
      migration: 'Use analytics prop instead.'
    }],
    props: {
      enableAnalytics: enableAnalytics
    }
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.productCtas, className])
  }, _hasCcCompareButton && /*#__PURE__*/React.createElement(CompareCardCheckbox, {
    card: {
      id: getLegacyId(marketplaceEntity)
    },
    elementStyle: CHECKBOX_STYLE.BUTTON,
    analytics: {
      enabled: !!analytics
    },
    handleCompare: _handleCcCompareClick,
    addText: _ccCompareButtonTextBeforeClick,
    removeText: _ccCompareButtonTextAfterClick
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.productLinkAndSecondaryCta
  }, /*#__PURE__*/React.createElement(MarketplaceApplyNow, {
    annotationText: ctaSubtext,
    className: styles.productLink,
    disableAnnotation: ctaSubtext === false,
    id: id,
    impressionId: impressionContext === null || impressionContext === void 0 ? void 0 : impressionContext.impressionId,
    link: ctaHref,
    linkType: APPLY_NOW_LINK_TYPES.applyNowButton,
    marketplaceEntity: marketplaceEntity,
    monetizable: getMonetizable(marketplaceEntity),
    productLocation: TRK_PRODUCT_LOCATION // TODO stop passing this through
    ,
    productPosition: trkProductPosition,
    reviewLink: reviewLinkHref,
    sectionName: (analytics === null || analytics === void 0 ? void 0 : analytics.sectionName) || COMPONENT_ANALYTICS_NAME,
    shouldLogImpression: shouldLogImpression,
    showIcon: ctaShowIcon,
    text: ctaText,
    trkPageType: trkPageType,
    trkSectionCategory: buildTrkSectionCategory(trkSectionCategory, 'cta'),
    trkTaxonomy: taxonomy,
    type: LINK_TYPES.BUTTON // BNK specific props.
    ,
    displayPage: displayPage
  }), secondaryCtaText && secondaryCtaHref && /*#__PURE__*/React.createElement(MarketplaceApplyNow, {
    className: styles.secondaryCta,
    finishType: APPLY_NOW_FINISH_TYPES.EXTERNAL_CONTENT,
    impressionId: impressionContext === null || impressionContext === void 0 ? void 0 : impressionContext.impressionId,
    id: APPLY_NOW_LINK_TYPES.ratesAndFeesText,
    link: secondaryCtaHref,
    linkType: APPLY_NOW_LINK_TYPES.ratesAndFeesText,
    marketplaceEntity: marketplaceEntity,
    monetizable: getMonetizable(marketplaceEntity),
    sectionName: analytics === null || analytics === void 0 ? void 0 : (_analytics$productImp = analytics.productImpressionProps) === null || _analytics$productImp === void 0 ? void 0 : (_analytics$productImp2 = _analytics$productImp.payload) === null || _analytics$productImp2 === void 0 ? void 0 : _analytics$productImp2.section_name,
    sectionPosition: analytics === null || analytics === void 0 ? void 0 : (_analytics$productImp3 = analytics.productImpressionProps) === null || _analytics$productImp3 === void 0 ? void 0 : (_analytics$productImp4 = _analytics$productImp3.payload) === null || _analytics$productImp4 === void 0 ? void 0 : _analytics$productImp4.section_position,
    type: LINK_TYPES.LINK,
    shouldLogImpression: shouldLogImpression
  }, secondaryCtaText)));
};

ProductCtas.propTypes = {
  className: PropTypes.string,
  ctaHref: PropTypes.string,
  ctaShowIcon: PropTypes.bool,
  ctaSubtext: PropTypes.string,
  ctaText: PropTypes.string,
  id: PropTypes.string,
  marketplaceEntity: PropTypes.object.isRequired,
  // eslint-disable-line react/forbid-prop-types
  reviewLinkHref: PropTypes.string,
  secondaryCtaHref: PropTypes.string,
  secondaryCtaText: PropTypes.string,
  shouldLogImpression: PropTypes.bool,
  taxonomy: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  trkPageType: PropTypes.string,
  trkProductPosition: PropTypes.number,
  trkSectionCategory: PropTypes.string,
  // BNK specific props.
  displayPage: PropTypes.string,
  // CC specific props
  _hasCcCompareButton: PropTypes.bool,
  _handleCcCompareClick: PropTypes.func,
  _ccCompareButtonTextBeforeClick: PropTypes.string,
  // custom compare button text prop
  _ccCompareButtonTextAfterClick: PropTypes.string,
  // analytics
  analytics: PropTypes.shape({
    sectionName: PropTypes.string,
    // Props passed down from the product impression used for Rates & Fees links
    payload: PropTypes.shape({
      element_position: PropTypes.number,
      section_name: PropTypes.string,
      section_position: PropTypes.number
    }),
    productImpressionId: PropTypes.string,
    productImpressionProps: PropTypes.shape({
      payload: PropTypes.shape({
        element_position: PropTypes.number,
        // Replace this with the productImpressionId above
        parent_impression_id: PropTypes.string,
        section_name: PropTypes.string,
        section_position: PropTypes.number
      })
    }).isRequired
  }),
  // deprecated
  enableAnalytics: PropTypes.bool
};
ProductCtas.defaultProps = {
  // BNK specific props.
  displayPage: '',
  // CC specific props
  _hasCcCompareButton: false,
  _handleCcCompareClick: function _handleCcCompareClick() {}
};
export default ProductCtas;